/**
 * ThemeNotification.jsx — One-time tooltip that points users to the ThemeToggle.
 *
 * The notification slides in from the top-right corner 2 seconds after the
 * component mounts. Once the user closes it, 'theme-notification-closed' is
 * written to localStorage so it never appears again on this browser.
 *
 * Exports: ThemeNotification
 */
import { useState, useEffect } from 'react';
import { Moon, Sun, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * ThemeNotification — Animated hint banner that introduces the theme toggle.
 * Renders nothing after the user has dismissed it once.
 */
export function ThemeNotification() {
  // show: controls whether the notification is visible. Starts false so the
  // 2-second delay works correctly on first load.
  const [show, setShow] = useState(false);

  // On mount: check if the user has already dismissed the notification.
  // If not, schedule it to appear after 2 seconds.
  useEffect(() => {
    // Check if user has closed the notification
    const hasClosedNotification = localStorage.getItem('theme-notification-closed');

    if (!hasClosedNotification) {
      // Show after 2 seconds and keep visible until closed
      const timer = setTimeout(() => {
        setShow(true);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, []);

  /**
   * Hides the notification and persists the dismissal to localStorage so it
   * does not appear again on future page loads.
   */
  const handleClose = () => {
    setShow(false);
    localStorage.setItem('theme-notification-closed', 'true');
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: -20, x: 20 }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          exit={{ opacity: 0, y: -20, x: 20 }}
          className="fixed top-20 right-6 z-50 max-w-sm"
        >
          <div className="bg-card border shadow-2xl rounded-2xl p-4 flex items-start gap-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="flex items-center gap-2 bg-primary/10 rounded-full px-3 py-1.5">
                <Sun className="h-4 w-4 text-primary" />
                <Moon className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium mb-1">Theme Toggle</p>
                <p className="text-xs text-muted-foreground">
                  Click the icon above to switch themes
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
